import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { INK, FONT } from './vizTokens';

/**
 * A horizontal bar chart, built as plain DOM.
 *
 * Horizontal because every category here has a long name ("Pending contract
 * upload", "HR & ADMIN") — rotated column labels are the usual way these charts
 * become unreadable.
 *
 * Marks follow the fixed spec: capped thickness with the band's leftover left as
 * air, a 4px rounded data-end with the baseline end square, values direct-labelled
 * at the tip, and a recessive hairline baseline. One series, so no legend — the
 * heading says what is plotted.
 */
const BarChart = ({ rows, colors, onSelect, selected, emptyText = 'Nothing to show' }) => {
  if (!rows || rows.length === 0) {
    return (
      <Typography sx={{ ...FONT, color: INK.muted, fontSize: '13px', py: 2 }}>
        {emptyText}
      </Typography>
    );
  }

  // Scale to the largest bar, never to the total: the reader is comparing
  // categories with each other, and a total-scaled axis flattens them all.
  const max = Math.max(...rows.map((r) => r.count), 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {rows.map((row, i) => {
        // A row may carry its own colour, as a status bar does among a ramp.
        const color = row.color || (Array.isArray(colors) ? colors[i % colors.length] : colors);
        const pct = (row.count / max) * 100;
        const clickable = Boolean(onSelect);
        const isSelected = selected != null && selected === row.key;
        // A zero row still gets a visible stub, so "nothing is here" reads as a
        // deliberate zero rather than a missing row.
        const width = row.count === 0 ? 0 : Math.max(pct, 2);

        return (
          <Tooltip
            key={row.key}
            title={`${row.label}: ${row.count.toLocaleString()}`}
            placement="top"
            arrow
          >
            <Box
              onClick={clickable ? () => onSelect(row) : undefined}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '110px 1fr 44px', sm: '150px 1fr 52px' },
                alignItems: 'center',
                gap: 1.5,
                cursor: clickable ? 'pointer' : 'default',
                borderRadius: '6px',
                px: 0.75,
                py: 0.5,
                mx: -0.75,
                backgroundColor: isSelected ? INK.plane : 'transparent',
                transition: 'background-color 120ms ease',
                '&:hover': clickable ? { backgroundColor: INK.plane } : undefined,
              }}
            >
              <Typography
                sx={{
                  ...FONT,
                  fontSize: '13px',
                  // Labels wear text tokens, never the series colour.
                  color: isSelected ? INK.primary : INK.secondary,
                  fontWeight: isSelected ? 600 : 400,
                  lineHeight: 1.3,
                }}
              >
                {row.label}
              </Typography>

              {/* The track is the band; the bar is capped inside it so the
                  leftover height stays as air rather than being filled. */}
              <Box sx={{ position: 'relative', height: '20px', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    height: '14px',
                    width: `${width}%`,
                    backgroundColor: color,
                    // Square at the baseline, 4px rounded at the data end.
                    borderRadius: '0 4px 4px 0',
                    minWidth: row.count === 0 ? 0 : '4px',
                    transition: 'width 260ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </Box>

              <Typography
                sx={{
                  ...FONT,
                  fontSize: '13px',
                  fontWeight: 600,
                  color: row.count === 0 ? INK.muted : INK.primary,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.count.toLocaleString()}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default BarChart;
